import { DateTime } from 'luxon';
import i18next from 'i18next';
import type { TFunction } from 'i18next';
import { matchIsValidTel } from 'mui-tel-input';
import z from 'zod';

import { TripLegType } from '@/data/models/reservation.model';

import { useDefaultTranslation } from '@/hooks/useCommonTranslation';

const isValidUSPhone = (message: string) =>
  z
    .string()
    .refine(
      (v) =>
        matchIsValidTel(v, { onlyCountries: ['US'] }) &&
        v !== '+1',
      { message },
    );

/** Google Places / passenger autocomplete value (see AddressInput PlaceType) */
const placeTypeSchema = z
  .object({
    description: z.string().optional(),
    mainText: z.string().optional(),
    secondaryText: z.string().optional(),
    mainTextMatchedSubstrings: z
      .array(z.object({ offset: z.number(), length: z.number() }))
      .optional(),
    place_id: z.string().optional(),
    zipCode: z.string().optional(),
    isHeader: z.boolean().optional(),
  })
  .catchall(z.unknown());

const addressTripFieldSchema = (requiredMessage: string) =>
  z
    .union([z.string(), placeTypeSchema])
    .superRefine((val, ctx) => {
      if (typeof val === 'string') {
        if (!val.trim()) {
          ctx.addIssue({ code: 'custom', message: requiredMessage });
        }
        return;
      }
      if (val.isHeader === true || val.place_id === 'header') {
        ctx.addIssue({ code: 'custom', message: requiredMessage });
        return;
      }
      const hasAddress =
        Boolean(val.place_id?.trim()) ||
        Boolean(val.mainText?.trim()) ||
        Boolean(val.description?.trim());
      if (!hasAddress) {
        ctx.addIssue({ code: 'custom', message: requiredMessage });
      }
    });

const tripSegmentSchema = z
  .object({
    reasonForTrip: z.string().min(1, 'Reason for trip is required'),
    appointmentDate: z.preprocess(
      (val) => {
        if (val === null || val === undefined) return '';
        if (val instanceof DateTime && val.isValid) return val.toISODate();
        return val;
      },
      z
        .string()
        .optional()
        .default(''),
    ),
    isWillCall: z.boolean(),
    appointmentDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    pickupDate: z.preprocess(
      (val) => {
        if (val === null || val === undefined) return '';
        if (val instanceof DateTime && val.isValid) return val.toISODate();
        return val;
      },
      z
        .string()
        .optional()
        .default(''),
    ),
    pickupDateTime: z
      .union([
        z.null(),
        z.custom<DateTime>((val) => val instanceof DateTime && val.isValid),
      ])
      .optional(),
    pickupAddress: addressTripFieldSchema('Pickup address is required'),
    dropoffAddress: addressTripFieldSchema('Drop-off address is required'),
    pickupPhone: isValidUSPhone('Pickup phone is required'),
    pickupPhoneExtension: z.string().optional().default(''),
    dropoffPhone: z.union([isValidUSPhone('Drop-off phone is required'), z.string().optional().default('')]),
    dropoffPhoneExtension: z.string().optional().default(''),
    pickupNotes: z.string().optional().default(''),
    dropoffNotes: z.string().optional().default(''),
    roundTripWillCall: z.boolean(),
    standingOrder: z.boolean().optional().default(false),
    passengerMobility: z.string().min(1, 'Passenger mobility is required'),
    passengerAssistance: z.string().optional(),
    AttributeLevel1: z.string().min(1, 'Attribute level 1 is required'),
    AttributeLevel2: z.string().optional().default(''),
    AttributeLevel3: z.string().optional(),
    AttributeLevel4: z.string().optional(),
    AttributeLevel5: z.string().optional(),
    AttributeLevel6: z.string().optional(),
    MobilityAttribute: z.string().min(1, 'Mobility attribute is required'),
    height: z.string().optional().default(''),
    weight: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    if (data.isWillCall) {
      if (!data.appointmentDate || data.appointmentDate.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Appointment date is required for will-call trips',
          path: ['appointmentDate'],
        });
      }
      if (!data.pickupDate || data.pickupDate.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Pickup date is required for will-call trips',
          path: ['pickupDate'],
        });
      }
      return;
    }

    const appt = data.appointmentDateTime;
    if (appt === null || appt === undefined || !(appt instanceof DateTime && appt.isValid)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Appointment date and time is required',
        path: ['appointmentDateTime'],
      });
    }
    const pickup = data.pickupDateTime;
    if (pickup === null || pickup === undefined || !(pickup instanceof DateTime && pickup.isValid)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pickup date and time is required',
        path: ['pickupDateTime'],
      });
    }
  });

const isAppointmentDateRequired = (isWillCall: boolean) => isWillCall;

const isAppointmentDateTimeRequired = (isWillCall: boolean) => !isWillCall;

const isPickupDateTimeRequired = (isWillCall: boolean) => !isWillCall;

type TripSegmentValues = z.infer<typeof tripSegmentSchema>;

const DEFAULT_TRIP_TYPE = TripLegType.RoundTrip;

const emptyTripSegment: z.input<typeof tripSegmentSchema> = {
  reasonForTrip: '',
  appointmentDate: null,
  isWillCall: false,
  appointmentDateTime: null,
  pickupDate: null,
  pickupDateTime: null,
  pickupAddress: '',
  dropoffAddress: '',
  pickupPhone: '+1',
  pickupPhoneExtension: '',
  dropoffPhone: '+1',
  dropoffPhoneExtension: '',
  pickupNotes: '',
  dropoffNotes: '',
  roundTripWillCall: true,
  standingOrder: false,
  passengerMobility: '',
  passengerAssistance: '',
  AttributeLevel1: '',
  AttributeLevel2: '',
  AttributeLevel3: '',
  AttributeLevel4: '',
  AttributeLevel5: '',
  AttributeLevel6: '',
  MobilityAttribute: '',
  height: '',
  weight: '',
};

/**
 * When the reverse map includes GURNEY / BARIATRIC_GURNEY UUIDs, member weight is
 * required for trips whose AttributeLevel2 matches. Otherwise (e.g. machine-only
 * parse without the map), non-empty height indicates the gurney measurements UI.
 */
function tripRequiresMemberWeight(
  trip: { AttributeLevel2: string; height: string },
  reverseTransportationAttributeMap: Record<string, string> | null | undefined,
): boolean {
  const gurneyUuid = reverseTransportationAttributeMap?.['GURNEY'];
  const bariatricGurneyUuid =
    reverseTransportationAttributeMap?.['BARIATRIC_GURNEY'];
  const hasGurneyCodesInMap =
    gurneyUuid !== undefined || bariatricGurneyUuid !== undefined;

  if (hasGurneyCodesInMap) {
    return (
      (gurneyUuid !== undefined && trip.AttributeLevel2 === gurneyUuid) ||
      (bariatricGurneyUuid !== undefined &&
        trip.AttributeLevel2 === bariatricGurneyUuid)
    );
  }
  return Boolean(trip.height?.trim());
}

function createReservationFormSchema(
  reverseTransportationAttributeMap?: Record<string, string> | null,
  t?: TFunction<'common'>,
  isNonAmbulatoryByIndex?: boolean[] | null,
) {
  const translate = t ?? useDefaultTranslation;
  return z
    .object({
      tripType: z.nativeEnum(TripLegType),
      trips: z
        .array(tripSegmentSchema)
        .min(1, 'At least one trip is required'),
    })
    .superRefine((data, ctx) => {
      if (data.tripType !== TripLegType.MultiLeg && data.trips.length !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'One-way mode expects exactly one trip segment',
          path: ['trips'],
        });
      }
    })
    .superRefine((data, ctx) => {
      data.trips.forEach((trip, tripIndex) => {
        if (!tripRequiresMemberWeight(trip, reverseTransportationAttributeMap)) {
          return;
        }
        const w = String(trip.weight ?? '').trim();
        if (!w) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.requiredField'),
            path: ['trips', tripIndex, 'weight'],
          });
          return;
        }
        const num = parseInt(w, 10);
        if (Number.isNaN(num) || num < 1) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.memberWeightPositive'),
            path: ['trips', tripIndex, 'weight'],
          });
          return;
        }
        if (num > 999) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.weightValidation'),
            path: ['trips', tripIndex, 'weight'],
          });
        }
      });
    })
    .superRefine((data, ctx) => {
      if (isNonAmbulatoryByIndex == null) return;
      data.trips.forEach((trip, tripIndex) => {
        if (isNonAmbulatoryByIndex[tripIndex] !== true) return;
        if (!String(trip.AttributeLevel2 ?? '').trim()) {
          ctx.addIssue({
            code: 'custom',
            message: translate('reservation.requiredField'),
            path: ['trips', tripIndex, 'AttributeLevel2'],
          });
        }
      });
    });
}

const reservationFormSchema = createReservationFormSchema(null);

type CreatedReservationFormSchema = ReturnType<typeof createReservationFormSchema>;

type ReservationFormValues = z.infer<typeof reservationFormSchema>;
type ReservationFormInput = z.input<typeof reservationFormSchema>;

const emptyReservationFormValues: ReservationFormInput = {
  tripType: DEFAULT_TRIP_TYPE,
  trips: [emptyTripSegment],
};

export {
  type CreatedReservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
  type TripSegmentValues,
  DEFAULT_TRIP_TYPE,
  createReservationFormSchema,
  reservationFormSchema,
  emptyReservationFormValues,
  emptyTripSegment,
  isAppointmentDateRequired,
  isAppointmentDateTimeRequired,
  isPickupDateTimeRequired,
}